import React, { useMemo, useState } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import { ChevronDownIcon } from 'lucide-react';
import { categories } from '../data/collections';
import { mainNav } from '../data/navigation';
import { fromPrice, productsForCollection, resolveCollection } from '../utils/products';
import { ProductCard } from '../components/shop/ProductCard';
import { NotFound } from './NotFound';
import { useSeo } from '../hooks/useSeo';
import { breadcrumbJsonLd, truncate } from '../utils/seo';
import { site } from '../data/site';
import type { CategoryId, Collection as CollectionType } from '../types/product';
type Sort = 'populair' | 'prijs-op' | 'prijs-af' | 'beoordeling';
const sortLabels: Record<Sort, string> = {
  populair: 'Populairst',
  'prijs-op': 'Prijs: laag naar hoog',
  'prijs-af': 'Prijs: hoog naar laag',
  beoordeling: 'Best beoordeeld'
};
export function Collection() {
  const {
    slug = 'alles'
  } = useParams();
  const collection = resolveCollection(slug);
  if (!collection) return <NotFound data-id="element-483" data-mp="15k5oqd86di4t" />;
  return <CollectionView key={slug} collection={collection} data-id="element-484" data-mp="15knohju1qehh" />;
}
function CollectionView({
  collection
}: {
  collection: CollectionType;
}) {
  const [category, setCategory] = useState<CategoryId | 'alle'>('alle');
  const [sort, setSort] = useState<Sort>('populair');
  const all = productsForCollection(collection);
  const present = categories.filter(c => all.some(p => p.category === c.id));
  const seoText = collection.seoText ?? `Shop ${collection.title.toLowerCase()} bij AMIRA Beauty: ${all.map(p => p.name).join(', ')}. Pure Marokkaanse verzorging met een duidelijke beschrijving, werking en gebruiksaanwijzing per product.`;
  useSeo({
    title: `${collection.title} – Marokkaanse verzorging | AMIRA Beauty`,
    description: truncate(`${collection.intro} Shop ${all.slice(0, 3).map(p => p.name).join(', ')} en meer. Gratis verzending vanaf € 50.`),
    path: `/collectie/${collection.slug}`,
    image: collection.image ?? all[0]?.image,
    jsonLd: [{
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: collection.title,
      description: collection.intro,
      url: `${site.url}/collectie/${collection.slug}`,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: all.map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `${site.url}/product/${p.slug}`,
          name: p.name
        }))
      }
    }, breadcrumbJsonLd([{
      name: 'Home',
      path: '/'
    }, {
      name: collection.title,
      path: `/collectie/${collection.slug}`
    }])]
  });
  const visible = useMemo(() => {
    const list = category === 'alle' ? all : all.filter(p => p.category === category);
    const sorted = [...list];
    if (sort === 'prijs-op') sorted.sort((a, b) => fromPrice(a) - fromPrice(b));
    if (sort === 'prijs-af') sorted.sort((a, b) => fromPrice(b) - fromPrice(a));
    if (sort === 'beoordeling') sorted.sort((a, b) => b.rating - a.rating || b.reviews - a.reviews);
    return sorted;
  }, [all, category, sort]);
  return <div className="mx-auto max-w-7xl px-5 pb-24 pt-8 lg:px-8 lg:pt-10" data-id="element-485" data-mp="1h4ga055a8ts9">
      <nav aria-label="Kruimelpad" className="text-sm text-muted" data-id="element-486" data-mp="1lyarr31buyop">
        <Link to="/" className="hover:text-ink" data-id="element-487" data-mp="186nuazvnfk4c">Home</Link>
        <span className="mx-2" data-id="element-488" data-mp="1d4pclil93fer">/</span>
        <span className="text-ink" data-id="element-489" data-mp="1d5r0wora1yef">{collection.title}</span>
      </nav>

      <div className="mt-6 grid gap-8 lg:grid-cols-12 lg:items-end" data-id="element-490" data-mp="20ujonxxqwzxy">
        <div className="lg:col-span-7" data-id="element-491" data-mp="20s5tlp32fnqe">
          <h1 className="font-display text-4xl uppercase tracking-display text-ink sm:text-6xl" data-id="element-492" data-mp="24wdltw8qfltd">{collection.title}</h1>
          <p className="mt-3 max-w-xl text-lg text-muted" data-id="element-493" data-mp="1qv18lskyhyb6">{collection.intro}</p>
        </div>
        {collection.image && collection.kind === 'audience' && <img src={collection.image} alt="" className="hidden aspect-[16/9] w-full rounded-2xl object-cover object-[50%_25%] lg:col-span-5 lg:block" data-id="element-494" data-mp="fgxf0nia6c6n" />}
      </div>

      <div className="no-scrollbar -mx-5 mt-8 flex gap-2 overflow-x-auto px-5 lg:mx-0 lg:px-0" data-id="element-495" data-mp="kdxjwv3e98w6">
        {mainNav.map(n => <NavLink key={n.to} to={n.to} className={({
        isActive
      }) => `whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150 ${isActive ? 'bg-ink text-canvas' : 'bg-surface text-ink hover:bg-line'}`} data-id="element-496" data-mp="uz73abodrhes">
            {n.label}
          </NavLink>)}
      </div>

      <div className="mt-8 flex flex-col gap-4 border-y border-line py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-497" data-mp="1emeiuct9esug">
        {collection.kind !== 'category' && present.length > 1 ? <div className="no-scrollbar -mx-5 flex gap-1 overflow-x-auto px-5 sm:mx-0 sm:px-0" role="group" aria-label="Filter op categorie" data-id="element-498" data-mp="14p0e3agp7shz">
            {[{
          id: 'alle' as const,
          title: 'Alles'
        }, ...present].map(c => <button key={c.id} onClick={() => setCategory(c.id)} aria-pressed={category === c.id} className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors duration-150 ${category === c.id ? 'bg-surface font-medium text-ink' : 'text-muted hover:text-ink'}`} data-id="element-499" data-mp="3pepptem3uy6g">
                {c.title}
              </button>)}
          </div> : <p className="text-sm text-muted" data-id="element-500" data-mp="1wxkwh46vse4">{visible.length} producten</p>}
        <div className="flex items-center justify-between gap-4 sm:justify-end" data-id="element-501" data-mp="6uz5j6h9jzrp">
          {collection.kind !== 'category' && present.length > 1 && <p className="text-sm text-muted" data-id="element-502" data-mp="bvn36azzzeuw">{visible.length} producten</p>}
          <label className="relative" data-id="element-503" data-mp="gtont0d2nm8h">
            <span className="sr-only" data-id="element-504" data-mp="gsnasqchwpo4">Sorteren</span>
            <select value={sort} onChange={e => setSort(e.target.value as Sort)} className="h-10 appearance-none rounded-full border border-line bg-white pl-4 pr-10 text-sm font-medium text-ink focus:border-ink focus:outline-none" data-id="element-505" data-mp="2dm89dgd2n8wg">
              {(Object.keys(sortLabels) as Sort[]).map(s => <option key={s} value={s} data-id="element-506" data-mp="25n3p7bxu6s5t">{sortLabels[s]}</option>)}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" data-id="element-507" data-mp="2kg6vuo9w3lhr" />
          </label>
        </div>
      </div>

      {visible.length === 0 ? <div className="py-24 text-center" data-id="element-508" data-mp="2tgvclqqo3zyc">
          <p className="text-lg font-semibold" data-id="element-509" data-mp="2okmy8fkaoas8">Geen producten in deze selectie</p>
          <button onClick={() => setCategory('alle')} className="mt-4 text-sm font-medium underline underline-offset-4" data-id="element-510" data-mp="338hqjuue4d4s">
            Toon alles
          </button>
        </div> : <ul className="mt-10 grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-3 lg:grid-cols-4 lg:gap-x-6" data-id="element-511" data-mp="3c9fphypz1ugi">
          {visible.map(p => <li key={p.slug} data-id="element-512" data-mp="388vup1svf2xj">
              <ProductCard product={p} data-id="element-513" data-mp="385rfl9dvams3" />
            </li>)}
        </ul>}

      <section className="mt-24 max-w-3xl border-t border-line pt-10" data-id="element-514" data-mp="3qwxvd0f9sv4i">
        <h2 className="font-display text-3xl uppercase tracking-display text-ink" data-id="element-515" data-mp="3vu6hfc3jhjb1">Over {collection.title.toLowerCase()}</h2>
        <p className="mt-4 text-[17px] font-light leading-relaxed text-muted" data-id="element-516" data-mp="3huyh2fvn4559">{seoText}</p>
      </section>
    </div>;
}